import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider } from "./auth/AuthContext";
import { useTheme } from "./theme/ThemeContext";
import { AuthGuard } from "./auth/AuthGuard";
import { AppLayout } from "./components/AppLayout";
import { Login } from "./pages/Login";
import { Setup } from "./pages/Setup";
import { Invite } from "./pages/Invite";
import { Dashboard } from "./pages/Dashboard";
import { AgentsList } from "./pages/Agents/List";
import { AgentDetail } from "./pages/Agents/Detail";
import { AgentConfig } from "./pages/Agents/Config";
import { AgentExec } from "./pages/Agents/Exec";
import { UsersList } from "./pages/Users/List";
import { UserDetail } from "./pages/Users/Detail";
import { RolesList } from "./pages/Roles/List";
import { RoleDetail } from "./pages/Roles/Detail";
import { ProcessesList } from "./pages/Processes/List";
import { ProcessDetail } from "./pages/Processes/Detail";
import { ProcessInstanceDetail } from "./pages/ProcessInstances/Detail";
import { CasesList } from "./pages/Cases/List";
import { CaseDetail } from "./pages/Cases/Detail";
import { ChannelsList } from "./pages/Channels/List";
import { ChannelDetail } from "./pages/Channels/Detail";
import { NodesList } from "./pages/Nodes/List";
import { NodeDetail } from "./pages/Nodes/Detail";
import { BrainImagesList } from "./pages/BrainImages/List";
import { BrainImageDetail } from "./pages/BrainImages/Detail";
import { SettingsProfilesList } from "./pages/SettingsProfiles/List";
import { SettingsProfileDetail } from "./pages/SettingsProfiles/Detail";
import { AttachmentsList } from "./pages/Attachments/List";
import { AnthropicAuthPage } from "./pages/AnthropicAuth";
import { ConnectorsList } from "./pages/Connectors/List";
import { ConnectorDetail } from "./pages/Connectors/Detail";
import { PluginsList } from "./pages/Plugins/List";
import { PluginsDetail } from "./pages/Plugins/Detail";
import { RepositoriesPage } from "./pages/Repositories";
import { WorkspaceTemplatesList } from "./pages/WorkspaceTemplates/List";
import { PluginDetail } from "./pages/Plugins/PluginDetail";
import { WorkspaceTemplateDetail } from "./pages/WorkspaceTemplates/Detail";
import { PlatformSettingsPage } from "./pages/PlatformSettings";
import { LicensePage } from "./pages/License";
import { AuditList } from "./pages/Audit/List";
import { createQueryClient } from "./api/queryClient";

// QueryClient außerhalb der Komponente — sonst neu instanziiert bei jedem
// Re-Render. Single Instance pro App-Lifetime.
const queryClient = createQueryClient();

export function App() {
  const { theme } = useTheme();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Toaster theme={theme} position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/setup" element={<Setup />} />
          {/* #343 (UC-9): Einladung des Erst-Admins einlösen — public, Link aus dem Portal */}
          <Route path="/einladung" element={<Invite />} />
          <Route element={<AuthGuard />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/agents" element={<AgentsList />} />
              <Route path="/agents/:id" element={<AgentDetail />} />
              <Route path="/agents/:id/config" element={<AgentConfig />} />
              <Route path="/agents/:id/exec" element={<AgentExec />} />
              <Route path="/users" element={<UsersList />} />
              <Route path="/users/:id" element={<UserDetail />} />
              {/* #274: Fach-Rollen + kuratierte System-Rollen */}
              <Route path="/roles" element={<RolesList />} />
              <Route path="/roles/:id" element={<RoleDetail />} />
              {/* #282: Prozess-Registry (Betreiber-Sicht) */}
              <Route path="/processes" element={<ProcessesList />} />
              <Route path="/processes/:id" element={<ProcessDetail />} />
              <Route path="/process-instances/:id" element={<ProcessInstanceDetail />} />
              {/* #301 (UC-5b): Executions-Sicht — Läufe überleben ihre Instanz, deshalb eigener Bereich */}
              <Route path="/cases" element={<CasesList />} />
              <Route path="/cases/:id" element={<CaseDetail />} />
              <Route path="/channels" element={<ChannelsList />} />
              <Route path="/channels/:id" element={<ChannelDetail />} />
              <Route path="/nodes" element={<NodesList />} />
              <Route path="/nodes/:id" element={<NodeDetail />} />
              <Route path="/brain-images" element={<BrainImagesList />} />
              <Route path="/brain-images/:id" element={<BrainImageDetail />} />
              <Route path="/settings-profiles" element={<SettingsProfilesList />} />
              <Route path="/settings-profiles/:id" element={<SettingsProfileDetail />} />
              <Route path="/attachments" element={<AttachmentsList />} />
              <Route path="/anthropic-auth" element={<AnthropicAuthPage />} />
              <Route path="/connectors" element={<ConnectorsList />} />
              <Route path="/connectors/:id" element={<ConnectorDetail />} />
              <Route path="/plugins" element={<PluginsList />} />
              <Route path="/plugins/marketplaces/:id" element={<PluginsDetail />} />
              {/* #290 (UC-3): Plugin-Detail — EIN Ort für ein Plugin */}
              <Route path="/plugins/marketplaces/:id/:pluginName" element={<PluginDetail />} />
              {/* #290 (UC-2): Fach-Sicht Workspace-Vorlagen (Entity-Ebene über dem Repo) */}
              <Route path="/workspace-templates" element={<WorkspaceTemplatesList />} />
              <Route path="/workspace-templates/:id" element={<WorkspaceTemplateDetail />} />
              <Route path="/repositories" element={<RepositoriesPage />} />
              <Route path="/platform-settings" element={<PlatformSettingsPage />} />
              <Route path="/license" element={<LicensePage />} />
              <Route path="/audit" element={<AuditList />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  );
}
